import { C1_BaseObject } from "./C1_BaseObject";
import { C1_Room } from "./C1_Room";
import { C1_Wall } from "./C1_Wall";
import type { C1_FloorData, C1_FloorPdfSource, C1_PointData, C1_RoomData, C1_WallData } from "../types/C1_ModelTypes";
import type { C1_Opening } from "./C1_Opening";
import type Floor from "../../../src/models/Floor";
import Room from "../../../src/models/Room";
import { c1_state } from "./C1_runtimeContext";
import BaseModel from "../../../src/models/BaseModel";
import ObjectFactory from "../../../src/models/ObjectFactory";
import { replaceMaterializedEloDefinitionTree } from "./C1_materializeEloDefinition";
import { createEquipmentInstance } from "./C1_ensureEloEquipmentDefaults";
import { getRoomEloItemProfile } from "../data/C1_eloItemsRegistry";
import { transformFreeWallPoint } from "../furniture/C1_freeWallGeometry";

export function findOpeningInFloor(floor: C1_Floor, id: string): C1_Opening | undefined {
  for (const room of floor.rooms) {
    const opening = room.openings.find((candidate) => candidate.id === id);
    if (opening) return opening;
  }
  return undefined;
}

export class C1_Floor extends C1_BaseObject<C1_FloorData> {
  override get owner(): Floor | undefined {
    return super.owner as Floor | undefined;
  }

  get rooms(): C1_Room[] {
    return this.owner?.rooms.asArray().flatMap((room) => {
      const point = room.c1?.data.startPoint;
      return room.c1 && point && Number.isFinite(point.x) && Number.isFinite(point.y) ? [room.c1] : [];
    }) ?? [];
  }

  get freeWalls(): C1_Wall[] { return this.owner?.freeWalls?.asArray() ?? []; }

  /** Used by interactive placement and PDF imports alike; coordinates are plan metres. */
  addFreeWall(data: { start: C1_PointData; end: C1_PointData }): C1_Wall {
    if (!this.owner) throw new Error("Eine freie Wand benötigt eine Etage.");
    if (![data.start.x, data.start.y, data.end.x, data.end.y].every(Number.isFinite) ||
      Math.hypot(data.end.x - data.start.x, data.end.y - data.start.y) < 0.01)
      throw new Error("Ungültige Endpunkte für eine freie Wand.");
    const wall = new C1_Wall({ ...data, freeWall: true, visible: true, exterior: false }, this.owner);
    this.owner.freeWalls.add(wall);
    return wall;
  }

  /** Move attachments in the same undo step as the wall, retaining their wall reference. */
  moveFreeWall(wall: C1_Wall, start: C1_PointData, end: C1_PointData): void {
    if (!this.freeWalls.includes(wall)) throw new Error("Die Wand gehört nicht zu dieser Etage.");
    const before = { start: { ...wall.start }, end: { ...wall.end } };
    const angle = (Math.atan2(end.y - start.y, end.x - start.x) -
      Math.atan2(before.end.y - before.start.y, before.end.x - before.start.x)) * 180 / Math.PI;
    const transform = (anchor: C1_PointData) => {
      const sign = (before.end.x - before.start.x) * (anchor.y - before.start.y) -
        (before.end.y - before.start.y) * (anchor.x - before.start.x) >= 0 ? 1 : -1;
      const dx = (end.x - start.x) * sign, dy = (end.y - start.y) * sign;
      const side = Math.abs(dx) >= Math.abs(dy) ? dx >= 0 ? "top" as const : "bottom" as const : dy >= 0 ? "right" as const : "left" as const;
      return { side, position: transformFreeWallPoint(anchor, before, { start, end }) };
    };
    const attachments = this.rooms.flatMap(room => room.furnitures.filter(item => item.dockWall === wall).map(item => {
      const rect = room.innerRect();
      return { item, rect, ...transform({ x: rect.x1 + item.relativePosition.x * (rect.x2 - rect.x1),
        y: rect.y1 + item.relativePosition.y * (rect.y2 - rect.y1) }) };
    }));
    const devices = this.rooms.flatMap(room => (room.interactiveEloItems ?? []).flatMap(item => {
      const placement = item.data.interactivePlacement;
      const anchor = item.independentAbsolutePosition;
      return placement?._wallId === wall.id && anchor ? [{ item, placement, rect: room.innerRect(), ...transform(anchor) }] : [];
    }));
    wall.setEndpoints(start, end);
    for (const { item, rect, side, position } of attachments) {
      item.setPosition({ x: (position.x - rect.x1) / (rect.x2 - rect.x1 || 1),
        y: (position.y - rect.y1) / (rect.y2 - rect.y1 || 1) }, side, item.reflect, item.wallDocked, wall.id);
      item.setBaseProps({ ...item.baseProps, rotation: ((item.baseProps.rotation ?? 0) + angle + 360) % 360 });
    }
    for (const { item, placement, rect, side, position } of devices) item.setInteractivePlacement({
      ...placement, side, rotation: (placement.rotation + angle + 360) % 360,
      relativePosition: { x: (position.x - rect.x1) / (rect.x2 - rect.x1 || 1), y: (position.y - rect.y1) / (rect.y2 - rect.y1 || 1) },
    });
  }
  /** Transient UI state: marks the optional reference floor below the current floor. */
  visible = false;

  get className(): string {
    return "C1_Floor";
  }

  constructor(data: Partial<C1_FloorData>, owner: BaseModel) {
    super(data, owner);
  }

  get level() {
    return this.data.level;
  }

  get ownCabinet() {
    return this.data.ownCabinet;
  }

  get pdfSource(): C1_FloorPdfSource | undefined {
    return this.data.pdfSource;
  }

  setPdfSource(source: C1_FloorPdfSource): void {
    this.beforeChange("pdfSource");
    this._data.pdfSource = { ...source, originPdf: { ...source.originPdf } };
    this.changed("pdfSource");
  }

  /** Align an original PDF viewport point with the room geometry imported from that page. */
  pdfPointToFloor(point: C1_PointData): C1_PointData | undefined {
    const source = this.pdfSource;
    if (!source) return undefined;
    const metresPerPoint = (source.scale * 0.0254) / 72;
    return { x: (point.x - source.originPdf.x) * metresPerPoint, y: (point.y - source.originPdf.y) * metresPerPoint };
  }

  setOwnCabinet(ownCabinet: boolean) {
    if (ownCabinet === this.data.ownCabinet) return;
    this.beforeChange("ownCabinet");
    this._data.ownCabinet = ownCabinet;
    this.changed("ownCabinet");
  }

  setVisible(visible: boolean) {
    if (visible === this.visible) return;
    if (visible) {
      const project = c1_state.project;
      project?.floors
        .filter((floor) => floor.id !== this.id && floor.visible)
        .forEach((floor) => floor.setVisible(false));
    }
    this.visible = visible;
    this.changed("visible");
  }

  addRoom(data: C1_RoomData & { name: string; rect?: import("../types/C1_AppTypes").Rect; walls?: C1_WallData[] }) {
    const owner = this.owner;
    if (!owner) throw new Error("Cannot add a room to a C1 floor without an ESTADA owner.");

    const room = new Room({ name: data.name }, owner);
    const { rect, walls, ...roomData } = data;

    const eloTree = createEquipmentInstance(
      "room",
      c1_state.project.equipmentStandard,
      getRoomEloItemProfile(data.type),
      c1_state.settings.buttonDefaults,
    );
    room.c1 = new C1_Room(roomData, room);
    owner.rooms.add(room);
    if (walls?.length) {
      for (const wall of walls) room.walls.add(new C1_Wall({ end: { ...wall.end }, visible: wall.visible }, room));
      room.c1.recalculateFloorWallTypes();
    } else if (rect) room.c1.setRect(rect);
    replaceMaterializedEloDefinitionTree(room, eloTree);
    return room.c1;
  }

  removeRoom(id: string) {
    const room = this.owner?.rooms.findFirst((candidate) => candidate.id === id);
    if (!room) return;
    room.delete();
  }

  /** Adds geometry to an existing room while retaining its devices and model identity. */
  placeExistingRoom(id: string, data: Omit<C1_RoomData, "id">, rect: import("../types/C1_AppTypes").Rect): C1_Room {
    const room = this.owner?.rooms.findFirst(candidate => candidate.id === id);
    if (!room) throw new Error("The room to place no longer belongs to this floor.");
    if (room.c1?.data.startPoint && Number.isFinite(room.c1.data.startPoint.x) && Number.isFinite(room.c1.data.startPoint.y)) {
      throw new Error("The room is already placed.");
    }
    if (!room.c1) room.c1 = new C1_Room(data, room);
    room.c1.setRect(rect);
    return room.c1;
  }

  toJSON() {
    return {
      ...this.data,
      name: (this.owner?.name ?? ""),
      rooms: this.rooms.map((room) => room.toJSON()),
    };
  }
}

ObjectFactory.registerClass("c1:C1_Floor", (data, owner) => new C1_Floor(data, owner!) as unknown as BaseModel);
